import { TableSkeleton } from '@/components/ui/skeletons'
import { TableCheckbox } from '@/components/ui/table-checkbox'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'
import type { ExpandedState, OnChangeFn } from '@tanstack/react-table'
import {
	createColumnHelper,
	getCoreRowModel,
	getExpandedRowModel,
	getGroupedRowModel,
	getSortedRowModel,
	useReactTable,
} from '@tanstack/react-table'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TableView } from './TableView'
import type { CommonTableProps, ExtendedCellContext, SortingState, VisibilityState } from './types'
import { useCheckboxSelection } from './useCheckboxSelection'

export function CommonTable<TData>({
	data = [],
	columns,
	allData,
	totalCount,
	fetchAllSelectableIds,
	fetchSelectAllCount,
	resetSelectionOn,
	isLoading = false,
	error = null,
	loadingSkeleton,
	getRowId,
	enableSelection = false,
	selectionMode = 'multi',
	initialSelection = [],
	onSelectionChange,
	selectableRowFilter,
	disabledRowTooltip,
	disableSelectAll = false,
	selectAllDisabledTooltip,
	onSelectionStateChange,
	selectionToolbarProps,
	enableColumnReordering = false,
	frozenColumnIds,
	frozenRightColumnIds,
	staticColumnIds,
	initialColumnOrder,
	onColumnOrderChange,
	onRowClick,
	onRowDoubleClick,
	onRowRightClick,
	onRowKeySelect,
	onGroupRowKeySelect,
	highlightedRowId,
	getRowVariant,
	getRowClassName,
	enableGrouping = false,
	grouping = [],
	initialExpandedState,
	expanded,
	onExpandedChange,
	preserveSelectionToolbarInBottomBar = false,
	onScroll,
	dataTestId,
	manualSorting = false,
	sorting: controlledSorting,
	onSortingChange,
}: CommonTableProps<TData>) {
	const [sortingInternal, setSortingInternal] = useState<SortingState>([])

	// When manualSorting is on and a sorting prop is supplied, the sort state is
	// controlled by the caller (server-side sort); otherwise use internal client-side state.
	const isControlledSorting = manualSorting && controlledSorting !== undefined
	const sorting = isControlledSorting ? controlledSorting : sortingInternal
	const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
	// Frozen columns are pinned by `frozenColumnIds` and can't be dragged, so the order state and
	// everything persisted from it carry movable columns only. Their position never comes from storage.
	const frozenIds = useMemo(() => frozenColumnIds ?? [], [frozenColumnIds])
	const excludeFrozen = useCallback((ids: string[]) => ids.filter(id => !frozenIds.includes(id)), [frozenIds])
	// Read side of the same rule: a column that was movable when its order was saved can become
	// frozen later, since some pages vary `frozenColumnIds` by viewport or grouping. Dropping those
	// ids here keeps them out of the movable order, and out of what gets persisted back.
	const [columnOrder, setColumnOrder] = useState<string[]>(() => excludeFrozen(initialColumnOrder ?? []))

	// Display preferences hydrate after mount, so the saved order often arrives a render or two late.
	// Keyed by content, not identity: callers commonly pass a fresh array literal every render, and
	// re-adopting the same value would fight a drag the user just made.
	const savedOrderKey = excludeFrozen(initialColumnOrder ?? []).join('\u0000')
	const appliedOrderKeyRef = useRef<string | null>(null)
	useEffect(() => {
		if (savedOrderKey === appliedOrderKeyRef.current) {
			return
		}
		appliedOrderKeyRef.current = savedOrderKey
		setColumnOrder(savedOrderKey === '' ? [] : savedOrderKey.split('\u0000'))
	}, [savedOrderKey])
	// Use custom initial expanded state if provided, otherwise expand all groups
	const [expandedInternal, setExpandedInternal] = useState<ExpandedState>(initialExpandedState ?? true)

	const isControlledExpanded = expanded !== undefined
	const finalExpanded = isControlledExpanded ? expanded : expandedInternal
	const finalSetExpanded = isControlledExpanded ? onExpandedChange : setExpandedInternal

	// Track if we've initialized the expanded state to prevent re-expansion on data changes
	// This prevents groups from auto-expanding when user interacts with the table
	const hasInitializedExpanded = useRef(false)

	const isMobile = useIsMobile()

	// Track the previous initialExpandedState to detect meaningful changes
	const prevInitialExpandedStateRef = useRef<ExpandedState | undefined>(undefined)

	// Only expand all rows once when grouping is first enabled, not on every data change.
	// This prevents unwanted re-expansion when user clicks checkboxes or data updates.
	useEffect(() => {
		if (enableGrouping && !hasInitializedExpanded.current) {
			// Use custom initial expanded state if provided, otherwise expand all
			if (!isControlledExpanded) {
				setExpandedInternal(initialExpandedState ?? true)
			}
			hasInitializedExpanded.current = true
			prevInitialExpandedStateRef.current = initialExpandedState
		} else if (enableGrouping && hasInitializedExpanded.current) {
			// Check if initialExpandedState changed from `true` to a specific object
			// This handles the case where data loads after initial render
			const prevState = prevInitialExpandedStateRef.current
			if (prevState === true && typeof initialExpandedState === 'object' && initialExpandedState !== null) {
				if (!isControlledExpanded) {
					setExpandedInternal(initialExpandedState)
				}
				prevInitialExpandedStateRef.current = initialExpandedState
			}
		} else if (!enableGrouping) {
			// Reset the flag when grouping is disabled so it can initialize again when re-enabled
			hasInitializedExpanded.current = false
			prevInitialExpandedStateRef.current = undefined
		}
	}, [enableGrouping, initialExpandedState, isControlledExpanded])

	const rowIdFunction =
		getRowId ||
		((row: TData) => {
			return data.indexOf(row).toString()
		})

	const [autoPersistentIds, setAutoPersistentIds] = useState<string[]>([])

	const autoSelectionChange = useCallback(
		(
			selectedIds: string[],
			selectedData: TData[],
			action: 'select' | 'deselect' | 'selectAll' | 'deselectAll' | 'selectRange' | 'deselectRange',
		) => {
			setAutoPersistentIds(selectedIds)
			onSelectionChange?.(selectedIds, selectedData, action)
		},
		[onSelectionChange],
	)

	const effectiveInitialSelection = initialSelection.length ? initialSelection : autoPersistentIds
	const effectiveSelectionChange = onSelectionChange || autoSelectionChange

	const selectionState = useCheckboxSelection({
		data,
		getRowId: rowIdFunction,
		initialSelection: effectiveInitialSelection,
		onSelectionChange: effectiveSelectionChange,
		onSelectionStateChange,
		selectableRowFilter,
		allData,
		totalCount,
		fetchAllSelectableIds,
		fetchSelectAllCount,
		resetOn: resetSelectionOn,
	})

	const finalColumns = useMemo(() => {
		if (!enableSelection) {
			return columns
		}

		const columnHelper = createColumnHelper<TData>()

		const checkboxColumn = columnHelper.display({
			id: 'select',
			meta: { alignment: 'center' as const },
			header: () => {
				if (selectionMode === 'single') {
					return null // No select-all check box for single selection mode
				}
				// Every row's checkbox is disabled (e.g. RBAC denies all row actions), or the caller
				// explicitly says no bulk action exists at all (`disableSelectAll`, e.g. individual rows
				// remain selectable one at a time but multi-select would only ever lead to a dead-end
				// contextual menu) → the select-all is a no-op, so render it disabled rather than a
				// misleading toggle.
				const noSelectableRows = !selectionState.hasSelectableRows
				const selectAllDisabled = noSelectableRows || disableSelectAll
				// Server-backed select-all in flight — surface the wait and ignore toggles until it lands.
				const selectAllPending = selectionState.isSelectingAll
				// Only a plain string makes sense here — a per-row tooltip function has no row to resolve against.
				const headerTooltipMessage = selectAllDisabled
					? disableSelectAll
						? (selectAllDisabledTooltip ?? null)
						: typeof disabledRowTooltip === 'string'
							? disabledRowTooltip
							: null
					: null

				const headerCheckboxElement = (
					// Toggle runs on pointerup; the real <input> is visual-only.
					// Clicking fast re-renders the table and shifts this cell between
					// mousedown and mouseup, so the browser never fires `click` — and a
					// checkbox's `change` only fires together with `click`. That silently
					// dropped select-all clicks. `pointerup` always fires, so it can't.
					// biome-ignore lint/a11y/useSemanticElements: native checkbox kept visual-only on purpose; the wrapper is the reliable click/keyboard target
					<div
						role='checkbox'
						aria-checked={selectionState.isPartiallySelected ? 'mixed' : selectionState.isAllSelected}
						aria-disabled={selectAllDisabled}
						aria-busy={selectAllPending}
						aria-label='Select all rows'
						tabIndex={selectAllDisabled ? -1 : 0}
						data-header-checkbox='true'
						data-testid={dataTestId ? `${dataTestId}-select-all` : undefined}
						className={cn(
							'flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
							selectAllDisabled ? 'cursor-not-allowed' : selectAllPending ? 'cursor-wait' : 'cursor-pointer',
							selectAllDisabled || isMobile || selectAllPending || selectionState.selectedCount > 0
								? 'opacity-100'
								: 'opacity-0 group-hover:opacity-100',
						)}
						onPointerUp={event => {
							if (event.button !== 0 || selectAllDisabled || selectAllPending) {
								return
							}
							event.stopPropagation()
							selectionState.toggleAll()
						}}
						onClick={event => {
							// Toggle happens in onPointerUp; just stop row/document handlers.
							event.stopPropagation()
						}}
						onKeyDown={event => {
							if (event.key === ' ' || event.key === 'Enter') {
								event.preventDefault()
								event.stopPropagation()
								if (!selectAllDisabled && !selectAllPending) {
									selectionState.toggleAll()
								}
							}
						}}
					>
						<TableCheckbox
							checked={selectionState.isAllSelected}
							indeterminate={selectionState.isPartiallySelected}
							readOnly
							tabIndex={-1}
							aria-hidden='true'
							disabled={selectAllDisabled}
							className='pointer-events-none'
						/>
					</div>
				)

				return (
					<div className='flex items-center justify-center group w-full'>
						{headerTooltipMessage ? (
							<Tooltip>
								<TooltipTrigger asChild>{headerCheckboxElement}</TooltipTrigger>
								<TooltipContent side='right' sideOffset={8}>
									<p>{headerTooltipMessage}</p>
								</TooltipContent>
							</Tooltip>
						) : (
							headerCheckboxElement
						)}
					</div>
				)
			},

			cell: ({ row, ...context }) => {
				const rowId = getRowId ? getRowId(row.original) : row.id //tanstanck table auto generated id
				const isSelectable = selectionState.isRowSelectable(row.original)
				const rowIndex = (context as ExtendedCellContext).virtualIndex ?? row.index
				const isRowChecked = selectionState.isSelected(rowId)

				// Get tooltip message for disabled rows
				const tooltipMessage =
					!isSelectable && disabledRowTooltip
						? typeof disabledRowTooltip === 'function'
							? disabledRowTooltip(row.original)
							: disabledRowTooltip
						: null

				// Toggles the row. Called from the wrapper's pointerup (below), not from the
				// native checkbox, which is visual-only here.
				// Why pointerup: clicking fast re-renders the table and moves this cell between
				// mousedown and mouseup, so the browser skips `click` (and the checkbox `change`
				// that rides on it) and the toggle is lost. `pointerup` always fires, so it can't.
				const handleSelectToggle = (shiftPressed: boolean): void => {
					if (!isSelectable) {
						return
					}
					if (selectionMode === 'single') {
						if (isRowChecked) {
							selectionState.deselectRow(rowId)
						} else {
							selectionState.deselectAll()
							selectionState.selectRow(rowId, row.original) // clicking on unselected row deselects others and selects this one
						}
					} else {
						selectionState.toggleRow(
							rowId, // unique id
							row.original, // full row data object
							rowIndex, // position int
							shiftPressed,
						)
					}
				}

				const checkboxElement = (
					// biome-ignore lint/a11y/useSemanticElements: native checkbox kept visual-only on purpose; the wrapper is the reliable click/keyboard target
					<div
						role='checkbox'
						aria-checked={isRowChecked}
						aria-label='Select row'
						aria-disabled={!isSelectable}
						tabIndex={isSelectable ? 0 : -1}
						data-testid={dataTestId ? `${dataTestId}-row-checkbox-${rowId}` : undefined}
						className={cn(
							'flex items-center justify-center size-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
							isSelectable ? 'cursor-pointer' : 'cursor-not-allowed',
						)}
						onPointerUp={event => {
							// Primary button / touch only. Toggle here (not onClick) so re-renders
							// that move the cell between mousedown and mouseup can't drop the action.
							if (event.button !== 0) {
								return
							}
							event.stopPropagation()
							handleSelectToggle(event.shiftKey)
						}}
						onClick={event => {
							// Stop the row click handler / document click-away from firing.
							// The actual toggle happens in onPointerUp.
							event.stopPropagation()
						}}
						onDoubleClick={event => {
							// Rapid toggling must not trigger the row's double-click action.
							event.stopPropagation()
						}}
						onKeyDown={event => {
							if (event.key === ' ' || event.key === 'Enter') {
								event.preventDefault()
								event.stopPropagation()
								handleSelectToggle(event.shiftKey)
							}
						}}>
						<TableCheckbox
							checked={isRowChecked}
							readOnly
							tabIndex={-1}
							aria-hidden='true'
							disabled={!isSelectable}
							className='pointer-events-none'
						/>
					</div>
				)

				return (
					<div className='flex items-center justify-center size-full'>
						{tooltipMessage ? (
							<Tooltip>
								<TooltipTrigger asChild>{checkboxElement}</TooltipTrigger>
								<TooltipContent side='right' sideOffset={8}>
									<p>{tooltipMessage}</p>
								</TooltipContent>
							</Tooltip>
						) : (
							checkboxElement
						)}
					</div>
				)
			},
		})

		return [checkboxColumn, ...columns]
	}, [
		enableSelection,
		columns,
		selectionMode,
		selectionState,
		getRowId,
		isMobile,
		disabledRowTooltip,
		disableSelectAll,
		selectAllDisabledTooltip,
		dataTestId,
	])

	// A saved order is usually partial, and TanStack moves every id it lists to the front — which put a
	// newly enabled column right after the first one. Apply the saved order only to the slots those ids
	// already occupy, so columns with no saved entry keep their defined position.
	const effectiveColumnOrder = useMemo(() => {
		const liveIds = excludeFrozen(
			finalColumns.map(col => col.id || (col as { accessorKey?: string }).accessorKey || 'unknown'),
		)
		const liveIdSet = new Set(liveIds)
		const savedIds = columnOrder.filter(id => liveIdSet.has(id))
		if (savedIds.length === 0) {
			return [...frozenIds, ...liveIds]
		}
		const queue = [...savedIds]
		const savedSet = new Set(savedIds)
		const merged = liveIds.map(id => (savedSet.has(id) ? (queue.shift() as string) : id))
		return [...frozenIds, ...merged]
	}, [frozenIds, columnOrder, finalColumns, excludeFrozen])

	const handleColumnReorder = (activeId: string, overId: string) => {
		if (activeId === overId) {
			return
		}

		setColumnOrder(prevOrder => {
			// Drag against what is on screen — effectiveColumnOrder, not the partial saved order.
			const newOrder = excludeFrozen(effectiveColumnOrder)
			const activeIndex = newOrder.indexOf(activeId)
			const overIndex = newOrder.indexOf(overId)

			if (activeIndex === -1 || overIndex === -1) {
				return prevOrder
			}

			// Remove the active item and insert it at the new position
			const [movedItem] = newOrder.splice(activeIndex, 1)
			newOrder.splice(overIndex, 0, movedItem)

			onColumnOrderChange?.(newOrder)

			return newOrder
		})
	}

	// Row model order matters: core -> grouped -> sorted -> expanded
	const table = useReactTable({
		data,
		columns: finalColumns,
		getCoreRowModel: getCoreRowModel(),
		...(enableGrouping && {
			getGroupedRowModel: getGroupedRowModel(),
		}),
		// With manual sorting the server returns rows already ordered; skip the
		// client-side sorted row model so the received order is preserved.
		...(manualSorting ? { manualSorting: true } : { getSortedRowModel: getSortedRowModel() }),
		...(enableGrouping && {
			getExpandedRowModel: getExpandedRowModel(),
		}),
		// Prevent TanStack Table from auto-resetting expanded state when data/columns change
		// This preserves user's manual expand/collapse actions during interactions
		autoResetExpanded: false,
		onSortingChange: updater => {
			const next = typeof updater === 'function' ? updater(sorting) : updater
			if (!isControlledSorting) {
				setSortingInternal(next)
			}
			onSortingChange?.(next)
		},
		onColumnVisibilityChange: setColumnVisibility,
		onExpandedChange: finalSetExpanded as OnChangeFn<ExpandedState>,
		getRowId,
		initialState: {
			...(enableGrouping && {
				expanded: true,
			}),
		},
		state: {
			sorting,
			columnVisibility,
			columnOrder: effectiveColumnOrder,
			...(enableGrouping && {
				grouping,
				expanded: finalExpanded,
			}),
		},
	})

	if (isLoading) {
		return (
			loadingSkeleton ?? <TableSkeleton cols={columns.length} showCheckbox={enableSelection} className='size-full' />
		)
	}

	if (error) {
		return (
			<div className='border border-destructive/20 bg-destructive/10 text-destructive rounded-lg flex flex-1 min-h-0 items-center justify-center min-w-0'>
				<div className='text-center'>
					<div className='font-medium mb-2'>Error loading data</div>
					<div className='text-body opacity-90'>{error.message}</div>
				</div>
			</div>
		)
	}

	if (data.length === 0) {
		return (
			<div className='border border-dashed rounded-lg flex flex-1 min-h-0 items-center justify-center min-w-0'>
				<div className='text-center'>
					<div className='text-muted-foreground mb-2'>No data available</div>
					<div className='text-body text-muted-foreground'>Start by adding some data to see it here</div>
				</div>
			</div>
		)
	}

	return (
		<div
			data-testid={dataTestId}
			className='size-full flex flex-1 flex-col min-h-0 min-w-0 border overflow-hidden bg-card'>
			<TableView
				table={table}
				getRowId={getRowId}
				enableSelection={enableSelection}
				selectionState={enableSelection ? selectionState : undefined}
				selectionToolbarProps={selectionToolbarProps}
				frozenColumnIds={frozenColumnIds}
				frozenRightColumnIds={frozenRightColumnIds}
				staticColumnIds={staticColumnIds}
				enableColumnReordering={enableColumnReordering}
				onColumnReorder={handleColumnReorder}
				onRowClick={onRowClick}
				onRowDoubleClick={onRowDoubleClick}
				onRowRightClick={onRowRightClick}
				onRowKeySelect={onRowKeySelect}
				onGroupRowKeySelect={onGroupRowKeySelect}
				highlightedRowId={highlightedRowId}
				getRowVariant={getRowVariant}
				getRowClassName={getRowClassName}
				enableGrouping={enableGrouping}
				preserveSelectionToolbarInBottomBar={preserveSelectionToolbarInBottomBar}
				onScroll={onScroll}
				dataTestId={dataTestId}
			/>
		</div>
	)
}
